// src/services/api.js
import axios from 'axios';

// For Create React App, you should use REACT_APP_ prefixed environment variables
// They get injected at build time. Fallback to localhost if not defined.
const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

const api = axios.create({
    baseURL: BASE_URL,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    },
    timeout: 30000, // 30 seconds timeout
});

// Request interceptor to add token
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        // For FormData, let browser set Content-Type
        if (config.data instanceof FormData) {
            delete config.headers['Content-Type'];
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor for error handling
api.interceptors.response.use(
    (response) => {
        // Return the response as is, let components handle the structure
        return response;
    },
    (error) => {
        const originalRequest = error.config;

        // Handle 401 Unauthorized
        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            // Try to refresh token if we have a refresh token
            const refreshToken = localStorage.getItem('refresh_token');
            if (refreshToken) {
                return api.post('/auth/refresh', { refresh_token: refreshToken })
                    .then(response => {
                        if (response.data.status === 'success') {
                            const { token, refresh_token } = response.data.data;
                            localStorage.setItem('token', token);
                            if (refresh_token) {
                                localStorage.setItem('refresh_token', refresh_token);
                            }

                            // Retry original request with new token
                            api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
                            originalRequest.headers['Authorization'] = `Bearer ${token}`;
                            return api(originalRequest);
                        }
                        throw new Error('Token refresh failed');
                    })
                    .catch(refreshError => {
                        // Refresh failed, logout user
                        logoutUser();
                        return Promise.reject(refreshError);
                    });
            } else {
                logoutUser();
            }
        }

        // Handle 403 Forbidden
        if (error.response?.status === 403) {
            console.error('Access forbidden:', error.response.data);
        }

        // Handle 404 Not Found
        if (error.response?.status === 404) {
            console.error('Resource not found:', error.response.config.url);
        }

        // Handle 429 Too Many Requests
        if (error.response?.status === 429) {
            console.error('Rate limit exceeded');
        }

        // Handle 500 Server Errors
        if (error.response?.status >= 500) {
            console.error('Server error:', error.response.data);
        }

        return Promise.reject(error);
    }
);

// Helper function to logout user
const logoutUser = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    localStorage.removeItem('user_role');
    sessionStorage.clear();

    // Redirect to login page if we're not already there
    if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
    }
};

// Helper to extract data from different API response structures
const extractData = (response) => {
    if (!response || !response.data) {
        console.warn('Invalid response structure:', response);
        return null;
    }

    const data = response.data;

    // Handle different response structures
    if (data.status === 'success' && data.data !== undefined) {
        return data.data;
    }

    if (data.data !== undefined) {
        return data.data;
    }

    return data;
};

// Auth API
export const authAPI = {
    login: (credentials) => api.post('/auth/login', credentials),
    userLogin: (credentials) => api.post('/auth/user-login', credentials),
    vendorLogin: (credentials) => api.post('/auth/vendor-login', credentials),
    verify: () => api.get('/auth/verify'),
    logout: () => api.post('/auth/logout'),
    refresh: (refreshToken) => api.post('/auth/refresh', { refresh_token: refreshToken }),
    register: (userData) => api.post('/auth/register', userData),
    forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
    resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
};

// Dashboard API
export const dashboardAPI = {
    getStats: () => api.get('/dashboard'),
    getAnalytics: (params = {}) => api.get('/dashboard/analytics', { params }),
    getRecentActivities: (limit = 10) => api.get('/dashboard/activities', { params: { limit } }),
    getRevenueStats: (period = 'monthly') => api.get(`/dashboard/revenue?period=${period}`),
    getTopProducts: (limit = 5) => api.get(`/dashboard/top-products?limit=${limit}`),
};

// Users API
export const usersAPI = {
    list: (params = {}) => api.get('/users', { params }),
    create: (userData) => api.post('/users', userData),
    get: (userId) => api.get(`/users/${userId}`),
    getMe: () => api.get('/users/me'),
    update: (userId, userData) => api.put(`/users/${userId}`, userData),
    delete: (userId) => api.delete(`/users/${userId}`),
    updateStatus: (userId, status) => api.put(`/users/${userId}/status`, { status }),
    changePassword: (userId, passwordData) => api.put(`/users/${userId}/password`, passwordData),
    getStats: (userId) => api.get(`/users/${userId}/stats`),
    search: (query) => api.get('/users/search', { params: { q: query } }),
    bulkUpdate: (updates) => api.post('/users/bulk-update', { updates }),
};

// Vendors API
export const vendorsAPI = {
    list: (params = {}) => api.get('/vendors', { params }),
    create: (vendorData) => api.post('/vendors', vendorData),
    get: (vendorId) => api.get(`/vendors/${vendorId}`),
    update: (vendorId, vendorData) => api.put(`/vendors/${vendorId}`, vendorData),
    delete: (vendorId) => api.delete(`/vendors/${vendorId}`),
    updateStatus: (vendorId, status) => api.put(`/vendors/${vendorId}/status`, { status }),
    getProducts: (vendorId, params = {}) => api.get(`/vendors/${vendorId}/products`, { params }),
    getOrders: (vendorId, params = {}) => api.get(`/vendors/${vendorId}/orders`, { params }),
    getStats: (vendorId) => api.get(`/vendors/${vendorId}/stats`),
    getInventory: (vendorId) => api.get(`/vendors/${vendorId}/inventory`),
};

// Stores API
export const storesAPI = {
    list: (params = {}) => api.get('/stores', { params }),
    create: (storeData) => api.post('/stores', storeData),
    get: (storeId) => api.get(`/stores/${storeId}`),
    update: (storeId, storeData) => api.put(`/stores/${storeId}`, storeData),
    delete: (storeId) => api.delete(`/stores/${storeId}`),
    updateStatus: (storeId, status) => api.put(`/stores/${storeId}/status`, { status }),

    // Inventory
    getInventory: (storeId, params = {}) => api.get(`/stores/${storeId}/inventory`, { params }),
    getAvailableVariants: (storeId) => api.get(`/stores/${storeId}/available-variants`),
    addInventory: (storeId, inventoryData) => api.post(`/stores/${storeId}/inventory`, inventoryData),

    // Orders
    getOrders: (storeId, params = {}) => api.get(`/stores/${storeId}/orders`, { params }),

    // Stats
    getStats: (storeId) => api.get(`/stores/${storeId}/stats`),
    getDailyStats: (storeId, date) => api.get(`/stores/${storeId}/stats/daily`, { params: { date } }),

    // Vendors
    getVendors: (storeId) => api.get(`/stores/${storeId}/vendors`),
    assignVendor: (storeId, vendorId) => api.post(`/stores/${storeId}/vendors/${vendorId}`),
    removeVendor: (storeId, vendorId) => api.delete(`/stores/${storeId}/vendors/${vendorId}`),
};

// Products API
export const productsAPI = {
    list: (params = {}) => api.get('/products', { params }),
    create: (data) => {
        const isFormData = data instanceof FormData;
        return api.post('/products', data, {
            headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {}
        });
    },
    get: (productId) => api.get(`/products/${productId}`),
    update: (productId, data) => {
        const isFormData = data instanceof FormData;
        return api.put(`/products/${productId}`, data, {
            headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {}
        });
    },
    delete: (productId) => api.delete(`/products/${productId}`),
    updateStatus: (productId, status) => api.put(`/products/${productId}/status`, { status }),

    // Photo Management
    getPhotos: (productId) => api.get(`/products/${productId}/photos`),
    uploadPhotos: (productId, formData) => api.post(`/products/${productId}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    deletePhoto: (productId, photoId) => api.delete(`/products/${productId}/photos/${photoId}`),
    updatePhoto: (productId, photoId, photoData) => api.put(`/products/${productId}/photos/${photoId}`, photoData),
    setPrimaryPhoto: (productId, photoId) => api.put(`/products/${productId}/photos/${photoId}/primary`),

    // Variants
    getVariants: (productId, params = {}) => api.get(`/products/${productId}/variants`, { params }),
    createVariant: (productId, variantData) => api.post(`/products/${productId}/variants`, variantData),
    updateVariant: (productId, variantId, variantData) => api.put(`/variants/${variantId}`, variantData),
    deleteVariant: (productId, variantId) => api.delete(`/variants/${variantId}`),

    // Categories
    getCategories: () => api.get('/categories'),
    getSubcategories: (categoryId) => api.get(`/categories/${categoryId}/subcategories`),

    // Reviews
    getReviews: (productId, params = {}) => api.get(`/products/${productId}/reviews`, { params }),
    createReview: (productId, reviewData) => api.post(`/products/${productId}/reviews`, reviewData),
    updateReviewStatus: (productId, reviewId, status) => api.put(`/products/${productId}/reviews/${reviewId}/status`, { status }),
    deleteReview: (productId, reviewId) => api.delete(`/products/${productId}/reviews/${reviewId}`),

    // Inventory
    getInventory: (productId, params = {}) => api.get(`/products/${productId}/inventory`, { params }),

    // Search
    search: (query, params = {}) => api.get('/products/search', { params: { q: query, ...params } }),
};

// INVENTORY API - Enhanced with full CRUD operations
export const inventoryAPI = {
    // List all inventory with filters
    list: (params = {}) => api.get('/inventory', { params }),

    // Get inventory by store
    listByStore: (storeId, params = {}) => api.get(`/stores/${storeId}/inventory`, { params }),

    // Get inventory by product variant
    listByVariant: (variantId, params = {}) => api.get(`/variants/${variantId}/inventory`, { params }),

    // Get single inventory item
    get: (inventoryId) => api.get(`/inventory/${inventoryId}`),

    // Create new inventory item
    create: (inventoryData) => api.post('/inventory', inventoryData),

    // Update inventory item
    update: (inventoryId, inventoryData) => api.put(`/inventory/${inventoryId}`, inventoryData),

    // Delete inventory item
    delete: (inventoryId) => api.delete(`/inventory/${inventoryId}`),

    // Adjust inventory quantity
    adjust: (inventoryId, adjustmentData) => api.post(`/inventory/${inventoryId}/adjust`, adjustmentData),

    // Quick adjust (simple delta)
    quickAdjust: (inventoryId, delta, reason = 'Quick adjustment') =>
        api.post(`/inventory/${inventoryId}/adjust`, {
            quantity_delta: delta,
            reason
        }),

    // Bulk operations
    bulkUpdate: (updates) => api.post('/inventory/bulk-update', { updates }),
    bulkAdjust: (adjustments) => api.post('/inventory/bulk-adjust', { adjustments }),

    // History and reports
    getHistory: (inventoryId, params = {}) => api.get(`/inventory/${inventoryId}/history`, { params }),
    getLowStock: (threshold = 10) => api.get(`/inventory/low-stock?threshold=${threshold}`),
    getOutOfStock: () => api.get('/inventory/out-of-stock'),
    getStockAlerts: () => api.get('/inventory/alerts'),

    // Stock transfers between stores
    transfer: (transferData) => api.post('/inventory/transfer', transferData),
    getTransfers: (params = {}) => api.get('/inventory/transfers', { params }),
    getTransfer: (transferId) => api.get(`/inventory/transfers/${transferId}`),
    updateTransferStatus: (transferId, status) => api.put(`/inventory/transfers/${transferId}/status`, { status }),

    // Inventory counts/audits
    startCount: (countData) => api.post('/inventory/counts', countData),
    getCounts: (params = {}) => api.get('/inventory/counts', { params }),
    getCount: (countId) => api.get(`/inventory/counts/${countId}`),
    updateCount: (countId, countData) => api.put(`/inventory/counts/${countId}`, countData),
    completeCount: (countId) => api.post(`/inventory/counts/${countId}/complete`),

    // Stock levels by category
    getStockByCategory: (params = {}) => api.get('/inventory/stock-by-category', { params }),

    // Available variants for store
    getAvailableVariants: (storeId) => api.get(`/stores/${storeId}/available-variants`),

    // Check availability
    checkAvailability: (variantId, storeId, quantity = 1) =>
        api.get(`/inventory/check-availability`, {
            params: { variant_id: variantId, store_id: storeId, quantity }
        }),
};

// Variants API
export const variantsAPI = {
    list: (params = {}) => api.get('/variants', { params }),
    get: (variantId) => api.get(`/variants/${variantId}`),
    create: (variantData) => api.post('/variants', variantData),
    update: (variantId, variantData) => api.put(`/variants/${variantId}`, variantData),
    delete: (variantId) => api.delete(`/variants/${variantId}`),

    // Attributes
    getAttributes: (params = {}) => api.get('/attributes', { params }),
    getAttribute: (attributeId) => api.get(`/attributes/${attributeId}`),
    createAttribute: (attributeData) => api.post('/attributes', attributeData),
    updateAttribute: (attributeId, attributeData) => api.put(`/attributes/${attributeId}`, attributeData),
    deleteAttribute: (attributeId) => api.delete(`/attributes/${attributeId}`),

    // Attribute Values
    getAttributeValues: (attributeId) => api.get(`/attributes/${attributeId}/values`),
    createAttributeValue: (attributeId, valueData) => api.post(`/attributes/${attributeId}/values`, valueData),
    updateAttributeValue: (attributeId, valueId, valueData) => api.put(`/attributes/${attributeId}/values/${valueId}`, valueData),
    deleteAttributeValue: (attributeId, valueId) => api.delete(`/attributes/${attributeId}/values/${valueId}`),

    // Inventory for variant
    getInventory: (variantId, params = {}) => api.get(`/variants/${variantId}/inventory`, { params }),

    // Prices
    updatePrice: (variantId, priceData) => api.put(`/variants/${variantId}/price`, priceData),
    getPriceHistory: (variantId) => api.get(`/variants/${variantId}/price-history`),

    // Bulk operations
    bulkUpdate: (updates) => api.post('/variants/bulk-update', { updates }),
    bulkDelete: (variantIds) => api.post('/variants/bulk-delete', { variant_ids: variantIds }),
};

// Categories API
export const categoriesAPI = {
    list: (params = {}) => api.get('/categories', { params }),
    get: (categoryId) => api.get(`/categories/${categoryId}`),
    create: (categoryData) => {
        const isFormData = categoryData instanceof FormData;
        return api.post('/categories', categoryData, {
            headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {}
        });
    },
    update: (categoryId, categoryData) => {
        const isFormData = categoryData instanceof FormData;
        return api.put(`/categories/${categoryId}`, categoryData, {
            headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {}
        });
    },
    delete: (categoryId) => api.delete(`/categories/${categoryId}`),
    updateStatus: (categoryId, status) => api.put(`/categories/${categoryId}/status`, { status }),
    updatePosition: (categoryId, position) => api.put(`/categories/${categoryId}/position`, { position }),

    // Products in category
    getProducts: (categoryId, params = {}) => api.get(`/categories/${categoryId}/products`, { params }),

    // Subcategories
    getSubcategories: (categoryId, params = {}) => api.get(`/categories/${categoryId}/subcategories`, { params }),

    // Stats
    getStats: (categoryId) => api.get(`/categories/${categoryId}/stats`),
};

// Subcategories API
export const subcategoriesAPI = {
    list: (params = {}) => api.get('/subcategories', { params }),
    get: (subcategoryId) => api.get(`/subcategories/${subcategoryId}`),
    create: (subcategoryData) => {
        const isFormData = subcategoryData instanceof FormData;
        return api.post('/subcategories', subcategoryData, {
            headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {}
        });
    },
    update: (subcategoryId, subcategoryData) => {
        const isFormData = subcategoryData instanceof FormData;
        return api.put(`/subcategories/${subcategoryId}`, subcategoryData, {
            headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {}
        });
    },
    delete: (subcategoryId) => api.delete(`/subcategories/${subcategoryId}`),
    updateStatus: (subcategoryId, status) => api.put(`/subcategories/${subcategoryId}/status`, { status }),
    listByCategory: (categoryId, params = {}) => api.get(`/categories/${categoryId}/subcategories`, { params }),

    // Products in subcategory
    getProducts: (subcategoryId, params = {}) => api.get(`/subcategories/${subcategoryId}/products`, { params }),

    // Stats
    getStats: (subcategoryId) => api.get(`/subcategories/${subcategoryId}/stats`),
};

// Orders API
export const ordersAPI = {
    list: (params = {}) => api.get('/orders', { params }),
    get: (orderId) => api.get(`/orders/${orderId}`),
    create: (orderData) => api.post('/orders', orderData),
    update: (orderId, orderData) => api.put(`/orders/${orderId}`, orderData),
    delete: (orderId) => api.delete(`/orders/${orderId}`),

    // Status Management
    updateStatus: (orderId, status, notes = '') => api.put(`/orders/${orderId}/status`, { status, notes }),
    cancel: (orderId, reason = '') => api.post(`/orders/${orderId}/cancel`, { reason }),
    refund: (orderId, refundData) => api.post(`/orders/${orderId}/refund`, refundData),

    // Order Items
    getItems: (orderId) => api.get(`/orders/${orderId}/items`),
    updateItem: (orderId, itemId, itemData) => api.put(`/orders/${orderId}/items/${itemId}`, itemData),
    addItem: (orderId, itemData) => api.post(`/orders/${orderId}/items`, itemData),
    removeItem: (orderId, itemId) => api.delete(`/orders/${orderId}/items/${itemId}`),

    // Shipping
    updateShipping: (orderId, shippingData) => api.put(`/orders/${orderId}/shipping`, shippingData),
    getShippingMethods: () => api.get('/shipping/methods'),
    trackShipping: (orderId) => api.get(`/orders/${orderId}/tracking`),

    // Payments
    getPayment: (orderId) => api.get(`/orders/${orderId}/payment`),
    updatePayment: (orderId, paymentData) => api.put(`/orders/${orderId}/payment`, paymentData),
    capturePayment: (orderId) => api.post(`/orders/${orderId}/capture-payment`),
    refundPayment: (orderId, refundData) => api.post(`/orders/${orderId}/refund-payment`, refundData),

    // Cart operations
    placeOrder: (orderData) => api.post('/orders/place', orderData),
    checkInventory: (storeId) => api.get('/orders/inventory-status', { params: { store_id: storeId } }),
    ensureInventory: (storeId) => api.post('/orders/ensure-inventory', { store_id: storeId }),

    // Analytics
    getStats: (params = {}) => api.get('/orders/stats', { params }),
    getDailyStats: (date) => api.get(`/orders/stats/daily?date=${date}`),
    getRevenue: (params = {}) => api.get('/orders/revenue', { params }),

    // Export
    exportOrders: (params = {}) => api.get('/orders/export', {
        params,
        responseType: 'blob'
    }),
};

// Cart API
export const cartAPI = {
    get: () => api.get('/cart'),
    addItem: (itemData) => api.post('/cart/items', itemData),
    updateItem: (itemId, itemData) => api.put(`/cart/items/${itemId}`, itemData),
    removeItem: (itemId) => api.delete(`/cart/items/${itemId}`),
    clear: () => api.delete('/cart'),
    checkout: (checkoutData) => api.post('/cart/checkout', checkoutData),
    getCount: () => api.get('/cart/count'),
    applyCoupon: (code) => api.post('/cart/apply-coupon', { code }),
    removeCoupon: () => api.delete('/cart/remove-coupon'),
};

// Coupons API - UPDATED WITH ALL ENDPOINTS
export const couponsAPI = {
    // Create new coupon (Admin)
    create: (couponData) => api.post('/coupons', couponData),

    // Get ALL coupons with filters (Admin)
    list: (params = {}) => api.get('/coupons', { params }),

    // Get ONLY active valid coupons (Public)
    getActive: (params = {}) => api.get('/coupons/active', { params }),

    // Search coupons (Admin)
    search: (params = {}) => api.get('/coupons/search', { params }),

    // Get coupon statistics (Admin)
    stats: () => api.get('/coupons/stats'),

    // Get single coupon by ID (Admin)
    get: (couponId) => api.get(`/coupons/${couponId}`),

    // Update coupon (Admin)
    update: (couponId, couponData) => api.put(`/coupons/${couponId}`, couponData),

    // Delete coupon (Admin)
    delete: (couponId) => api.delete(`/coupons/${couponId}`),

    // Validate coupon code (Public)
    validate: (code) => api.post('/coupons/validate', { code }),

    // Activate coupon (Admin)
    activate: (couponId) => api.post(`/coupons/${couponId}/activate`),

    // Deactivate coupon (Admin)
    deactivate: (couponId) => api.post(`/coupons/${couponId}/deactivate`),

    // Legacy endpoints (keep for backward compatibility)
    getUsage: (couponId, params = {}) => api.get(`/coupons/${couponId}/usage`, { params }),
    generateCode: (pattern = '') => api.post('/coupons/generate', { pattern }),
};

// Banners API
export const bannersAPI = {
    list: (params = {}) => api.get('/banners', { params }),
    get: (bannerId) => api.get(`/banners/${bannerId}`),
    create: (bannerData) => {
        const isFormData = bannerData instanceof FormData;
        return api.post('/banners', bannerData, {
            headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {}
        });
    },
    update: (bannerId, bannerData) => {
        const isFormData = bannerData instanceof FormData;
        return api.put(`/banners/${bannerId}`, bannerData, {
            headers: isFormData ? { 'Content-Type': 'multipart/form-data' } : {}
        });
    },
    delete: (bannerId) => api.delete(`/banners/${bannerId}`),
    updateStatus: (bannerId, status) => api.put(`/banners/${bannerId}/status`, { status }),
    updatePosition: (bannerId, position) => api.put(`/banners/${bannerId}/position`, { position }),
    getActive: () => api.get('/banners/active'),
};

// Uploads API
export const uploadsAPI = {
    upload: (formData, type = 'image') => api.post(`/uploads?type=${type}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
    }),
    delete: (filename) => api.delete(`/uploads/${filename}`),
    list: (params = {}) => api.get('/uploads', { params }),
    getUrl: (filename) => `${BASE_URL}/uploads/${filename}`,
};

// Settings API
export const settingsAPI = {
    get: () => api.get('/settings'),
    update: (settings) => api.put('/settings', settings),
    getByKey: (key) => api.get(`/settings/${key}`),
    updateByKey: (key, value) => api.put(`/settings/${key}`, { value }),
    getPaymentMethods: () => api.get('/settings/payment-methods'),
    updatePaymentMethods: (methods) => api.put('/settings/payment-methods', { methods }),
    getShippingSettings: () => api.get('/settings/shipping'),
    updateShippingSettings: (settings) => api.put('/settings/shipping', settings),
    getTaxSettings: () => api.get('/settings/tax'),
    updateTaxSettings: (settings) => api.put('/settings/tax', settings),
};

// Reports API
export const reportsAPI = {
    // Sales Reports
    getSalesReport: (params = {}) => api.get('/reports/sales', { params }),
    getRevenueReport: (params = {}) => api.get('/reports/revenue', { params }),

    // Inventory Reports
    getInventoryReport: (params = {}) => api.get('/reports/inventory', { params }),
    getStockMovementReport: (params = {}) => api.get('/reports/stock-movement', { params }),

    // User Reports
    getUserReport: (params = {}) => api.get('/reports/users', { params }),
    getCustomerReport: (params = {}) => api.get('/reports/customers', { params }),

    // Vendor Reports
    getVendorReport: (params = {}) => api.get('/reports/vendors', { params }),
    getVendorPerformanceReport: (params = {}) => api.get('/reports/vendor-performance', { params }),

    // Product Reports
    getProductReport: (params = {}) => api.get('/reports/products', { params }),
    getProductPerformanceReport: (params = {}) => api.get('/reports/product-performance', { params }),

    // Coupon Reports
    getCouponReport: (params = {}) => api.get('/reports/coupons', { params }),

    // Generate and download
    generateReport: (type, params = {}) => api.post(`/reports/generate/${type}`, params),
    getReportStatus: (reportId) => api.get(`/reports/status/${reportId}`),
    downloadReport: (reportId) => api.get(`/reports/download/${reportId}`, { responseType: 'blob' }),

    // Export
    exportReport: (type, format = 'csv', params = {}) =>
        api.get(`/reports/export/${type}`, {
            params: { ...params, format },
            responseType: 'blob'
        }),
};

// Notifications API
export const notificationsAPI = {
    list: (params = {}) => api.get('/notifications', { params }),
    get: (notificationId) => api.get(`/notifications/${notificationId}`),
    markAsRead: (notificationId) => api.put(`/notifications/${notificationId}/read`),
    markAllAsRead: () => api.put('/notifications/read-all'),
    getUnreadCount: () => api.get('/notifications/unread-count'),
    delete: (notificationId) => api.delete(`/notifications/${notificationId}`),
    deleteAll: () => api.delete('/notifications'),
    getSettings: () => api.get('/notifications/settings'),
    updateSettings: (settings) => api.put('/notifications/settings', settings),
};

// Audit Logs API
export const auditLogsAPI = {
    list: (params = {}) => api.get('/audit-logs', { params }),
    get: (logId) => api.get(`/audit-logs/${logId}`),
    getUserLogs: (userId, params = {}) => api.get(`/audit-logs/user/${userId}`, { params }),
    search: (params = {}) => api.get('/audit-logs/search', { params }),
    export: (params = {}) => api.get('/audit-logs/export', {
        params,
        responseType: 'blob'
    }),
};

// Health API
export const healthAPI = {
    check: () => api.get('/health'),
    systemInfo: () => api.get('/health/system'),
    databaseStatus: () => api.get('/health/database'),
    cacheStatus: () => api.get('/health/cache'),
};

// Analytics API
export const analyticsAPI = {
    getDashboard: () => api.get('/analytics/dashboard'),
    getSalesTrend: (params = {}) => api.get('/analytics/sales-trend', { params }),
    getTopProducts: (params = {}) => api.get('/analytics/top-products', { params }),
    getCustomerAnalytics: (params = {}) => api.get('/analytics/customers', { params }),
    getInventoryAnalytics: (params = {}) => api.get('/analytics/inventory', { params }),
    getRevenueAnalytics: (params = {}) => api.get('/analytics/revenue', { params }),
    getConversionRates: (params = {}) => api.get('/analytics/conversion', { params }),
    getCouponAnalytics: (params = {}) => api.get('/analytics/coupons', { params }),
};

// File export helper
export const downloadFile = (data, filename, type = 'application/octet-stream') => {
    const blob = new Blob([data], { type });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

// Error handling utility
export const handleApiError = (error) => {
    console.error('API Error:', error);

    // Default error message
    let message = 'An unexpected error occurred';
    let status = 0;
    let data = null;
    let validationErrors = {};

    if (error.response) {
        // Server responded with error
        status = error.response.status;
        data = error.response.data;

        if (data?.message) {
            message = data.message;
        } else if (data?.error) {
            message = data.error;
        } else if (status === 400) {
            message = 'Bad request. Please check your input.';
        } else if (status === 401) {
            message = 'Unauthorized. Please login again.';
        } else if (status === 403) {
            message = 'Forbidden. You do not have permission to access this resource.';
        } else if (status === 404) {
            message = 'Resource not found.';
        } else if (status === 409) {
            message = 'Conflict. Resource already exists or cannot be modified.';
        } else if (status === 422) {
            message = 'Validation error. Please check your input.';
            if (data?.errors) {
                validationErrors = data.errors;
            }
        } else if (status === 429) {
            message = 'Too many requests. Please try again later.';
        } else if (status >= 500) {
            message = 'Server error. Please try again later.';
        }
    } else if (error.request) {
        // No response received
        message = 'Network error. Please check your internet connection.';
        status = 0;
    } else {
        // Request configuration error
        message = error.message || 'An error occurred while processing your request.';
        status = 0;
    }

    return {
        success: false,
        message,
        status,
        data,
        validationErrors,
        error: error
    };
};

// Helper to format error for display
export const formatError = (error) => {
    const handled = handleApiError(error);
    return handled.message;
};

// Helper for common API calls with error handling
export const apiCall = async (apiFunction, ...args) => {
    try {
        const response = await apiFunction(...args);
        return {
            success: true,
            data: extractData(response),
            response,
            status: response.status
        };
    } catch (error) {
        return handleApiError(error);
    }
};

// Helper for paginated requests
export const fetchPaginatedData = async (apiFunction, params = {}, initialData = []) => {
    try {
        const response = await apiFunction(params);
        const data = extractData(response);

        if (data && data.data && data.meta) {
            return {
                success: true,
                data: initialData.concat(data.data),
                pagination: data.meta,
                hasMore: data.meta.page < data.meta.pages,
                response
            };
        }

        return {
            success: true,
            data: initialData.concat(data || []),
            pagination: null,
            hasMore: false,
            response
        };
    } catch (error) {
        return handleApiError(error);
    }
};

// Helper to create FormData from object
export const createFormData = (data) => {
    const formData = new FormData();

    Object.keys(data).forEach(key => {
        const value = data[key];
        if (value !== null && value !== undefined) {
            if (Array.isArray(value)) {
                value.forEach(item => {
                    formData.append(`${key}[]`, item);
                });
            } else if (value instanceof File || value instanceof Blob) {
                formData.append(key, value);
            } else {
                formData.append(key, value.toString());
            }
        }
    });

    return formData;
};

export default api;